import cogoToast from 'cogo-toast';
import { omit } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useLocale } from '../../../../../i18n';
import WgApiError from '../../../../app/src/features/api/classes/WgApiError';
import { isConversationUrl } from '../../../../app/src/features/authentication/constants/SocialAuth';
import useAuth from '../../../../app/src/features/authentication/hooks/useAuth';
import useBrokerId from '../../../../common/src/hooks/query-params/useBrokerId';
import usePartyId from '../../../../common/src/hooks/query-params/usePartyId';
import usePreviousSessionId from '../../../../common/src/hooks/query-params/usePreviousSessionId';
import { getQueryParams } from '../../../../common/src/hooks/useQueryParams';
import { useFlowsDispatch } from '../../../../common/src/utils/globalVariables';
import { ConversationStatus } from '../../../../conversations/src/features/overview/types/OverviewTypes';
import { createFlowById } from '../../api/createFlowById';
import { hasSubGroups } from '../../config/flowsStructureConfig';
import {
  setLastPosition,
  setInitializationError,
} from '../../store/redux/slices/flowsMetaDataSlice';
import { addVisitedTag } from '../../store/redux/slices/flowsSlice';
import store from '../../store/redux/store';
import { NewCreateFlowPayload } from '../../types/ApiTypes';
import { FlowsResponse } from '../../types/FlowsTypes';
import useAnswerForQuestion from '../useAnswerForQuestion';
import { getFlowsGroupPosition } from '../useFlowsHelper';
import useFlowsConversation from './useFlowsConversation';
import { FlowFunctionParams } from './useFlowsInitNew';
import useFlowsInitRoutes from './useFlowsInitRoutes';

interface UseFlowsCreation {
  createFlow: (params: CreateFlowParams) => Promise<FlowsResponse | void>;
  resumeFlow: (params: FlowFunctionParams) => Promise<void>;
}

interface CreateFlowParams {
  flowId: string;
  payload: NewCreateFlowPayload;
  persist?: boolean;
}

/**
 * This hook contains 2 methods to create or resume a flow
 */
const useFlowsCreation = (): UseFlowsCreation => {
  const { t } = useTranslation();
  const { campaign } = useAuth();
  const flowsDispatch = useFlowsDispatch();
  const { userLang } = useLocale();
  const getAnswerForQuestion = useAnswerForQuestion();
  const { getConversation } = useFlowsConversation();
  const { pushFlowsRoute } = useFlowsInitRoutes();
  const qPartyId = usePartyId();
  const qPreviousSessionId = usePreviousSessionId();
  const qBrokerId = useBrokerId();
  const history = useHistory();
  const clientTimeZone = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone;

  const createFlow = async ({
    flowId,
    payload,
    persist = true,
  }: CreateFlowParams) => {
    const basePayload = getFlowsBasePayload();
    const email = getAnswerForQuestion('policy_holder.email')?.answer as string;
    const partyId = qPartyId || payload.party_id;
    const previousSessionId = qPreviousSessionId || payload.previous_session_id;

    let resp;

    try {
      resp = await createFlowById({
        flowId,
        payload: {
          ...omit(payload, ['session_id']),
          language: userLang,
          ...(partyId && { party_id: partyId }),
          ...(previousSessionId && { previous_session_id: previousSessionId }),
          ...(campaign && { campaign_id: campaign.short_id }),
          ...(isConversationUrl && { filtered: true }),
          ...(!persist && { persist }),
          ...(email && { email }),
          ...(clientTimeZone && { timezone: clientTimeZone }),
          ...basePayload,
        },
      });
    } catch (error) {
      const apiError = error as WgApiError;

      if (apiError.msg === 'FLOW_FILTER_NOT_FOUND')
        return history.replace({
          pathname: `/company-scan/already-completed`,
          search: qBrokerId ? `?broker_id=${qBrokerId}` : undefined,
        });

      // Handle the case where a flow session was already completed
      if (apiError.msg === 'NOT_FOUND' && apiError.rpc_code === -31999) {
        // Get the localized error message
        const errorMessage =
          apiError.detail?.[userLang] ||
          apiError.detail?.en ||
          apiError.detail?.nl ||
          apiError.message ||
          t('Something went wrong while receiving the questions');

        flowsDispatch(setInitializationError(errorMessage));
        return;
      }

      return cogoToast.error(
        t('Something went wrong while receiving the questions'),
      );
    }

    return resp;
  };

  const resumeFlow = async ({ flow, initParams }: FlowFunctionParams) => {
    if (!initParams.sessionId)
      return cogoToast.error(
        t('Something went wrong while receiving the questions'),
      );

    const lastConversation = await getConversation(initParams.sessionId);

    const sessionId = initParams.sessionId;
    const flowId = initParams.flowId;

    let groupPos;
    if (
      flow.last_answer &&
      lastConversation?.status !== ConversationStatus.COMPLETED
    ) {
      groupPos = getFlowsGroupPosition({
        flowId: initParams.flowId,
        questionnaire: store.getState().flows.questionnaire,
        groupId: flow.last_answer?.group_id,
      });
    }

    if (groupPos && groupPos >= 0 && !hasSubGroups(initParams.flowId)) {
      flow.answers.forEach(({ id }) => {
        flowsDispatch(addVisitedTag(id.split('.')[0].toUpperCase() || ''));
      });
      flowsDispatch(setLastPosition(groupPos));
    } else {
      flowsDispatch(setLastPosition(0));
    }

    pushFlowsRoute({
      flowId,
      sessionId: sessionId,
      partyId: qPartyId,
      brokerId: qBrokerId,
      position: groupPos,
    });
  };

  return {
    createFlow,
    resumeFlow,
  };
};

interface BasePayload {
  car_revision_id?: string;
  residence_revision_id?: string;
  family_revision_id?: string;
}

const getFlowsBasePayload = () => {
  const params = getQueryParams();

  const basePayload: BasePayload = {};

  const carRevisionId = params.get('car_risk_object_revision_id');
  const residenceRevisionId = params.get('residence_risk_object_revision_id');
  const familyRevisionId = params.get('family_risk_object_revision_id');

  if (carRevisionId) basePayload.car_revision_id = carRevisionId;

  if (residenceRevisionId)
    basePayload.residence_revision_id = residenceRevisionId;

  if (familyRevisionId) basePayload.family_revision_id = familyRevisionId;

  return basePayload;
};

export default useFlowsCreation;
